/**
 * Teaching Video API client.
 *
 * Typed wrappers around the teaching video endpoints introduced in Phase 2.
 * All functions use the shared apiClient axios instance so auth, refresh,
 * and error handling are handled centrally.
 *
 * Endpoint reference: backend/app/api/teaching_video_routes.py
 */

import apiClient from './api';
import { getErrorMessage } from '@/utils/errors';
import type { MediaTokenResponse } from './videoApi';
import type {
  TeachingVideoUploadResponse,
  TeachingVideoStatusResponse,
  TranscriptResponse,
  TemplateLinkResponse,
} from '@/types/teachingVideo';
import type { MaterializationResult, MaterializeRequest } from '@/types/api';

// ---------------------------------------------------------------------------
// Upload a teaching video or audio file
// ---------------------------------------------------------------------------

/**
 * Upload a single video or audio file for teaching analysis.
 *
 * Product Decision §4: Only one file — video OR audio, not both.
 * Returns the created TeachingVideo record ID.
 */
export async function uploadTeachingVideo(
  file: File,
  sport: string,
  movement: string
): Promise<TeachingVideoUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (sport.trim()) formData.append('sport', sport.trim());
  if (movement.trim()) formData.append('movement', movement.trim());

  try {
    const response = await apiClient.post<TeachingVideoUploadResponse>(
      '/teaching-videos/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'Failed to upload teaching video.'));
  }
}

// ---------------------------------------------------------------------------
// Submit pasted coaching notes (no file upload)
// ---------------------------------------------------------------------------

/**
 * Submit pasted coaching notes as text (Product Decision §5 — first-class input).
 * Bypasses Pass 1 (ASR); text feeds directly into Pass 2 extraction.
 */
export async function submitPasteNotes(
  text: string,
  sport: string,
  movement: string
): Promise<TeachingVideoUploadResponse> {
  const formData = new FormData();
  formData.append('text', text);
  if (sport.trim()) formData.append('sport', sport.trim());
  if (movement.trim()) formData.append('movement', movement.trim());

  try {
    const response = await apiClient.post<TeachingVideoUploadResponse>(
      '/teaching-videos/paste-notes',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'Failed to submit coaching notes.'));
  }
}

/**
 * Import a teaching transcript from a YouTube URL (YTI-4). The backend fetches
 * the transcript via a managed SaaS (so our server IP never touches YouTube) and
 * stores it as a paste-style TeachingVideo, reusing the same link + process flow.
 *
 * Throws a tagged error on 503 (feature dormant — steer to paste) and 502
 * (fetch failed). Callers can inspect `err.response?.status` to branch.
 */
export async function importTeachingFromYoutube(
  url: string,
  sport?: string,
  movement?: string,
): Promise<TeachingVideoUploadResponse> {
  const response = await apiClient.post<TeachingVideoUploadResponse>(
    '/teaching-videos/from-youtube',
    { url, sport: sport?.trim() || undefined, movement: movement?.trim() || undefined },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Trigger async Pass 1 + Pass 2 extraction
// ---------------------------------------------------------------------------

/**
 * Trigger asynchronous extraction for the uploaded teaching video.
 * Returns immediately (202) — poll getTeachingVideoResult() for completion.
 */
export async function triggerProcessing(id: string): Promise<{ status: string }> {
  try {
    const response = await apiClient.post<{ status: string }>(
      `/teaching-videos/${id}/process`
    );
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'Failed to start teaching video processing.'));
  }
}

// ---------------------------------------------------------------------------
// Poll status and fetch result
// ---------------------------------------------------------------------------

/**
 * Poll the processing status of a teaching video.
 * When status='completed', the response includes the full TeachingVideoResult.
 */
export async function getTeachingVideoResult(id: string): Promise<TeachingVideoStatusResponse> {
  try {
    const response = await apiClient.get<TeachingVideoStatusResponse>(
      `/teaching-videos/${id}/result`
    );
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'Failed to fetch teaching video result.'));
  }
}

// ---------------------------------------------------------------------------
// Turn accepted suggestions into ready-to-save checks (PR-3)
// ---------------------------------------------------------------------------

/**
 * Coach-facing text for `MATERIALIZE_FAILURE`, the one error this endpoint can
 * produce that the coach has to act on.
 *
 * What / why / how, per `docs/COACH_FACING_COPY_STANDARD.md` §1.
 */
export const MATERIALIZE_FAILURE =
  'We could not add those checks just now. Nothing was changed on your template. ' +
  'Try again, and if it keeps happening, add the checks yourself in the list below.';

/**
 * The server's own sentence for a failure, or `null` when there isn't one.
 *
 * The backend writes every coach-facing sentence for this endpoint from one
 * catalogue (`backend/app/config/coach_notices.yaml`, REFACTOR_PLAN §C.7), so when a response
 * body carries a message it is the better text and we show it. A TRANSPORT
 * failure has no such sentence: axios throws with its own message ("Network
 * Error", "Request failed with status code 502"), which is internal text and
 * must never reach a coach. `getErrorMessage` prefers a thrown Error's own
 * `message` over its fallback, so this guard is what keeps that branch out —
 * if the extracted text is literally the thrown error's message, it did not
 * come from the server and we use our own sentence instead.
 */
function serverAuthoredMessage(err: unknown): string | null {
  const body = (err as { response?: { data?: unknown } })?.response?.data;
  if (!body) return null;
  const extracted = getErrorMessage(err, '');
  if (!extracted) return null;
  if (err instanceof Error && extracted === err.message) return null;
  return extracted;
}

/**
 * Materialise the selected suggestions into annotations the template can save.
 *
 * This is the ONE converter. It replaced a client-side one that wrote field
 * names the backend model did not have, so the numbers a coach said out loud
 * were silently dropped (TV-3 / FE-1 / E2E-3). The server builds each
 * annotation by constructing the real Pydantic goal model, and returns a
 * coach-facing notice for anything it could not turn into a check — so an item
 * is either in `annotations[]` or in `dropped[]` with a reason, never neither
 * (E2E-5 / E2E-6).
 *
 * Contract and the `source_ref` join-key format: `MaterializationResult` in
 * `types/api.ts`. Backend: `services/suggestion_materialization.py`.
 *
 * The one refusal worth naming: a suggestion compiled by an older materialiser
 * comes back **409** with
 * `detail: { kind: "stale_suggestion", error: "stale_compiled_suggestion",
 * message, notice, stale_annotation_ids[], recompile_required: true,
 * expected_materializer_version }` (owner decision D10a). `detail.message` is
 * the backend's own recompile prompt, so `serverAuthoredMessage` below picks it
 * up and the coach reads it verbatim — this client adds no sentence of its own
 * for that case.
 */
export async function materializeSuggestions(
  teachingVideoId: string,
  request: MaterializeRequest,
): Promise<MaterializationResult> {
  try {
    const response = await apiClient.post<MaterializationResult>(
      `/teaching-videos/${teachingVideoId}/materialize`,
      request,
    );
    return response.data;
  } catch (err) {
    throw new Error(serverAuthoredMessage(err) ?? MATERIALIZE_FAILURE);
  }
}

// ---------------------------------------------------------------------------
// Athlete-facing transcript read
// ---------------------------------------------------------------------------

/**
 * Fetch the transcript of a teaching video.
 * Accessible by the owner coach, admins, and channel-member athletes.
 */
export async function getTeachingVideoTranscript(id: string): Promise<TranscriptResponse> {
  try {
    const response = await apiClient.get<TranscriptResponse>(
      `/teaching-videos/${id}/transcript`
    );
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'Failed to fetch teaching video transcript.'));
  }
}

// ---------------------------------------------------------------------------
// Link teaching video to template
// ---------------------------------------------------------------------------

/**
 * Link a completed teaching video to a template.
 * Both the template and the teaching video must be owned by the caller.
 */
export async function linkTeachingVideoToTemplate(
  templateId: string,
  teachingVideoId: string
): Promise<TemplateLinkResponse> {
  try {
    const response = await apiClient.post<TemplateLinkResponse>(
      `/templates/${templateId}/teaching-video`,
      { teaching_video_id: teachingVideoId }
    );
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'Failed to link teaching video to template.'));
  }
}

// ---------------------------------------------------------------------------
// Build the stream URL for athlete playback
// ---------------------------------------------------------------------------

/**
 * Build the streaming URL for a teaching video/audio file.
 * The stream endpoint is athlete-accessible (no paid gate).
 *
 * @deprecated `/teaching-videos/{id}/stream` now requires either a bearer
 * token or a signed `sig`/`exp` pair (media-auth security sprint, 2026-09-21
 * — see MEDIA_AUTH_CONTRACT.md); a bare `<video src>`/`<audio src>` built from
 * this URL will 401. Use `useTeachingMediaUrl(id)`
 * (frontend/src/hooks/useMediaUrl.ts) instead.
 */
export function getTeachingVideoStreamUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  return `${base}/api/teaching-videos/${id}/stream`;
}

// ---------------------------------------------------------------------------
// Signed media URLs (media-auth security sprint, 2026-09-21) — see
// MEDIA_AUTH_CONTRACT.md, kind="teaching".
// ---------------------------------------------------------------------------

/**
 * Mint a short-lived signed-URL token for a teaching video (kind="teaching").
 */
export async function fetchTeachingMediaToken(teachingVideoId: string): Promise<MediaTokenResponse> {
  const response = await apiClient.get<MediaTokenResponse>(
    `/teaching-videos/${teachingVideoId}/media-token`
  );
  return response.data;
}

/**
 * Build the signed streaming URL for a teaching video from a previously-minted token.
 */
export function buildSignedTeachingStreamUrl(teachingVideoId: string, token: MediaTokenResponse): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  return `${base}/api/teaching-videos/${teachingVideoId}/stream?sig=${token.sig}&exp=${token.exp}`;
}
